import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from '../redux/store'
import { Box, Chip, Tooltip, LinearProgress } from '@mui/material'
import { useSocketConnection } from '../hooks/useSocketConnection'
import { useNetworkStatus } from '../hooks/useNetworkStatus'
import ErrorIcon from '@mui/icons-material/Error'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt'
import SignalCellularOffIcon from '@mui/icons-material/SignalCellularOff'
import WarningIcon from '@mui/icons-material/Warning'

export const SocketHealthMonitor: React.FC = () => {
  const socketHealth = useSelector((state: RootState) => state.socketHealth)
  const { isAuthenticated, checkWebSocketStatus } = useSocketConnection()
  const networkStatus = useNetworkStatus()
  const [webSocketStatus, setWebSocketStatus] = useState('Closed')
  const [hasEverBeenConnected, setHasEverBeenConnected] = useState(false)

  useEffect(() => {
    setWebSocketStatus(checkWebSocketStatus())
  }, [isAuthenticated, checkWebSocketStatus])

  // Track if socket was ever connected
  useEffect(() => {
    if (isAuthenticated) {
      setHasEverBeenConnected(true)
    }
  }, [isAuthenticated])

  // Show warning if no connection - Industry standard: prominent banner
  if (!networkStatus.isOnline) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: '#ffebee',
          borderLeft: '4px solid #d32f2f',
          borderRadius: 0,
          mb: 0,
          transition: 'all 0.3s ease'
        }}
      >
        <CloudOffIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
        <span style={{ fontSize: '13px', color: '#b71c1c', fontWeight: '500' }}>
          No internet connection - Please check your network
        </span>
      </Box>
    )
  }

  // Don't show warning for slow connection - hidden per user request
  // if (networkStatus.isSlowConnection) {
  //   return null
  // }

  // Show WebSocket disconnection warning ONLY if it was previously connected
  if (hasEverBeenConnected && (!isAuthenticated || webSocketStatus === 'Closed')) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: '#ffebee',
          borderLeft: '4px solid #d32f2f',
          borderRadius: 0,
          mb: 0,
          transition: 'all 0.3s ease'
        }}
      >
        <SignalCellularOffIcon sx={{ color: '#d32f2f', fontSize: 18 }} />
        <span style={{ fontSize: '13px', color: '#b71c1c', fontWeight: '500' }}>
          Disconnected from server - Attempting to reconnect...
        </span>
      </Box>
    )
  }

  // Show ping timeout warning
  if (socketHealth.isPingTimedOut) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          backgroundColor: '#fff8e1',
          borderLeft: '4px solid #f57f17',
          borderRadius: 0,
          mb: 0,
          transition: 'all 0.3s ease'
        }}
      >
        <ErrorIcon sx={{ color: '#f57f17', fontSize: 18 }} />
        <span style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
          Connection timeout - Reconnecting...
        </span>
      </Box>
    )
  }

  return null
}
